import {
  Box,
  Link,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { PersonAvatar } from '../../shared/PersonAvatar';
import { MIN_FONT_SIZE } from '../../../theme/typography';
import { useAwayScope } from '../hooks/useAwayScope';
import { PanelCard } from './PanelCard';
import type { AwayRelation, AwayScope, TeamAbsenceThisWeekItem } from '../types';
import { calendarDay } from '../../../utils/calendarDay';

interface WhosAwayListProps {
  items: TeamAbsenceThisWeekItem[] | undefined;
  /**
   * Which population `items` describes on first paint — from the summary's
   * `widgets.awayScope`. Never inferred from the rows: an empty list carries no
   * relation tags to infer from, and that is exactly the case AC-40 exists for.
   */
  scope?: AwayScope;
  loading?: boolean;
  /** Hide rows beyond this; "See all" link offers the rest. */
  maxRows?: number;
  onSeeAll?: () => void;
}

/**
 * Row-caption wording for each relation (AC-37). Absent on `scope=all`.
 *
 * The `project` wording is the fallback only — where the payload names the
 * shared project, {@link relationCaption} quotes it instead. "On your project"
 * is true of a dozen people on a busy week and identifies none of them.
 */
const RELATION_CAPTION: Record<AwayRelation, string> = {
  self: 'You',
  project: 'On your project',
  report: 'In your team',
};

/**
 * Why this person is on the list, in as few words as the row has space for.
 *
 * A peer usually shares exactly one project, so the common case reads "On
 * Apollo Rebuild". Where they share several, the first is named and the rest
 * are counted — "On Apollo Rebuild +2" — rather than listing names the row
 * would only truncate.
 */
function relationCaption(item: TeamAbsenceThisWeekItem): string | undefined {
  if (!item.relation) return undefined;
  const projects = item.relationProjects;
  if (item.relation !== 'project' || !projects?.length) {
    return RELATION_CAPTION[item.relation];
  }
  const extra = projects.length - 1;
  return `On ${projects[0]}${extra > 0 ? ` +${extra}` : ''}`;
}

/**
 * Relation precedence — mirrors the server's `AWAY_RELATION_RANK`. An untagged
 * row sorts last rather than first: "we could not say why this person is here"
 * is the weakest claim on the reader's attention, not the strongest.
 */
const RELATION_RANK: Record<AwayRelation, number> = {
  self: 0,
  project: 1,
  report: 2,
};

/**
 * "Who's away this week" — 24px hashed avatar + name + relation/date caption,
 * with a soft pill of the leave type on the right.
 *
 * ## Scope
 *
 * A `My team / Everyone` toggle is **always** available (AC-38). This is a
 * relevance control, not an authorisation one: `GET /api/absences` is already
 * readable by every authenticated role, so `scope=all` shows what the reader
 * could reach anyway. Switching re-reads this widget alone — see
 * {@link useAwayScope}.
 *
 * ## Ordering
 *
 * Own leave, then project peers, then reports, each block by start date
 * ascending, with the relation on the row so the ordering explains itself
 * (AC-37). The server already sorts this way; the widget re-sorts because it is
 * the layer that slices to `maxRows`, and a visible top-five must not depend on
 * the wire order surviving intact.
 *
 * Tops out at `maxRows` (default 5) — when there are more, a "See all" link
 * surfaces under the list and navigates to `/team-absences`.
 */
export function WhosAwayList({
  items,
  scope: summaryScope,
  loading,
  maxRows = 5,
  onSeeAll,
}: WhosAwayListProps) {
  const { items: scoped, scope, switching, error, setScope } = useAwayScope(
    items,
    summaryScope,
  );

  const all = sortByRelationThenStart(scoped ?? []);
  const visible = all.slice(0, maxRows);
  const overflow = all.length - visible.length;
  const busy = loading || switching;

  return (
    <PanelCard
      title="Who's away this week"
      // A full-height control, so the header sizes to it rather than clipping.
      anchorAction={false}
      action={
        <ToggleButtonGroup
          size="small"
          exclusive
          value={scope}
          onChange={(_, next: AwayScope | null) => next && setScope(next)}
          disabled={switching}
          aria-label="Whose absences to show"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              py: 0.25,
              px: 1,
              // Same 32px touch floor `MyProjectsList` and
              // `UpcomingAllocationsList` set beside this widget. Padding alone
              // left these at 25px, which is under it at 375.
              minHeight: 32,
              // The floor from the design system. `TeamStatusToday`'s identical
              // toggle sits at 0.6875rem, which is under it; 0.2px apart is
              // invisible beside it and this one clears the rule.
              fontSize: MIN_FONT_SIZE,
            },
          }}
        >
          <ToggleButton value="mine">My team</ToggleButton>
          <ToggleButton value="all">Everyone</ToggleButton>
        </ToggleButtonGroup>
      }
      data-testid="widget-whos-away"
    >
      <Stack spacing="8px">
        {error && (
          <Typography sx={{ fontSize: '0.71875rem', color: 'error.main' }}>
            {error}
          </Typography>
        )}

        {visible.length === 0 ? (
          <EmptyState loading={busy} scope={scope} />
        ) : (
          visible.map((item) => <AbsenceRow key={item.id} item={item} />)
        )}

        {overflow > 0 && onSeeAll ? (
          <Link
            component="button"
            type="button"
            onClick={onSeeAll}
            underline="hover"
            sx={{
              alignSelf: 'flex-start',
              fontSize: '0.75rem',
              fontWeight: 600,
              // 19px of text is not a tap target. Grown vertically only, so the
              // link's hit area clears 32px without the underline widening.
              display: 'inline-flex',
              alignItems: 'center',
              minHeight: 32,
            }}
          >
            {/* No count: the server caps the list, so `all.length` is the page
                size, not the number of people away. */}
            See all →
          </Link>
        ) : null}
      </Stack>
    </PanelCard>
  );
}

/**
 * The empty state names the population it is describing (AC-40).
 *
 * A bare "Nobody is away this week" under the `My team` scope reads as an
 * organisation-wide claim — the reader has no way to tell that the widget only
 * looked at their own people. So the scoped copy says whose week it checked and
 * points at the toggle that widens it.
 */
function EmptyState({ loading, scope }: { loading?: boolean; scope: AwayScope }) {
  if (loading) {
    return (
      <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem', py: 2 }}>
        Loading…
      </Typography>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
        {scope === 'mine'
          ? 'Nobody in your team or on your projects is away this week.'
          : 'Nobody in the organisation is away this week.'}
      </Typography>
      {scope === 'mine' && (
        <Typography sx={{ color: 'text.disabled', fontSize: '0.71875rem', mt: 0.5 }}>
          Switch to <strong>Everyone</strong> for the whole organisation.
        </Typography>
      )}
    </Box>
  );
}

function AbsenceRow({ item }: { item: TeamAbsenceThisWeekItem }) {
  const dateRange = formatRange(item.startDate, item.endDate);
  const relation = relationCaption(item);
  // Relation first, then the dates — the relation is why this row is on the
  // list, and it is what explains the ordering above it.
  const caption = [relation, dateRange].filter(Boolean).join(' · ');

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <PersonAvatar name={item.employeeName} src={item.avatarUrl} size={24} fontSize="0.6875rem" />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '0.78125rem',
            color: 'text.primary',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.3,
          }}
        >
          {item.employeeName}
        </Typography>
        {/* Wraps rather than ellipsing. The caption now carries the relation as
            well as the dates, and at 375px an ellipsis ate the end of the date
            range — the row would say why somebody is listed but no longer when
            they are back. The name above still truncates on one line. */}
        <Typography
          sx={{
            fontSize: MIN_FONT_SIZE,
            color: 'text.disabled',
            lineHeight: 1.3,
          }}
        >
          {caption}
        </Typography>
      </Box>
      {item.type ? (
        <Box
          sx={(theme) => ({
            fontSize: '0.625rem',
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.surface.raised,
            px: '7px',
            py: '2px',
            borderRadius: 999,
            flexShrink: 0,
          })}
        >
          {item.type}
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * Own leave, then project peers, then reports; within each block, start date
 * ascending (AC-37). Sorting a copy — the caller's array is the summary's, and
 * mutating it would reorder a payload other widgets share.
 */
function sortByRelationThenStart(
  items: TeamAbsenceThisWeekItem[],
): TeamAbsenceThisWeekItem[] {
  return [...items].sort((a, b) => {
    const byRelation = relationRank(a.relation) - relationRank(b.relation);
    if (byRelation !== 0) return byRelation;
    return a.startDate.localeCompare(b.startDate);
  });
}

function relationRank(relation: AwayRelation | undefined): number {
  return relation ? RELATION_RANK[relation] : Number.MAX_SAFE_INTEGER;
}

function formatRange(startISO: string, endISO: string): string {
  // `calendarDay`, not `new Date()`. An absence's dates are Prisma `DateTime`s
  // standing for calendar days, so the API sends `2026-08-17T00:00:00.000Z`;
  // formatting that locally floors it to the viewer's day and every date in this
  // widget reads one day early west of Greenwich.
  const start = calendarDay(startISO);
  const end = calendarDay(endISO);
  if (!start || !end) {
    return '';
  }
  const fmt = (d: Date) =>
    d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
  if (sameDay(start, end)) return fmt(start);
  return `${fmt(start)} – ${fmt(end)}`;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
